import React, { useState } from 'react';
import { CollaboratorEntity } from '../types';
import { SecondaryButton, DangerButton } from './buttons';

interface DeleteCollaboratorModalProps {
  collaborator: CollaboratorEntity;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

const DeleteCollaboratorModal: React.FC<DeleteCollaboratorModalProps> = ({
  collaborator,
  isOpen,
  onClose,
  onConfirm
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirm();
    } catch (error) {
      console.error('Delete confirmation error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-surface-secondary rounded-lg p-6 w-full max-w-md mx-4 border border-state-error/20">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-content flex items-center">
            <svg className="w-5 h-5 mr-2 text-state-error" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            Delete Collaborator
          </h3>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="text-content-muted hover:text-content transition-colors disabled:opacity-50"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="mb-6">
          <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
            <h4 className="text-content font-medium mb-2">Collaborator Details:</h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-center text-content-secondary">
                <svg className="w-4 h-4 mr-2 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <strong className="mr-2">Name:</strong> {collaborator.name}
              </div>
              
              {collaborator.email && (
                <div className="flex items-center text-content-secondary">
                  <svg className="w-4 h-4 mr-2 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                  </svg>
                  <strong className="mr-2">Email:</strong> {collaborator.email}
                </div>
              )}
              
              {collaborator.ipiNumber && (
                <div className="flex items-center text-content-secondary">
                  <svg className="w-4 h-4 mr-2 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m4 0v10a2 2 0 01-2 2H5a2 2 0 01-2-2V4h16z" />
                  </svg>
                  <strong className="mr-2">IPI:</strong> {collaborator.ipiNumber}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex space-x-3">
          <SecondaryButton
            onClick={onClose}
            disabled={isLoading}
            className="flex-1"
          >
            Cancel
          </SecondaryButton>
          <DangerButton
            onClick={handleConfirm}
            disabled={isLoading}
            loading={isLoading}
            className="flex-1"
          >
            Delete Collaborator
          </DangerButton>
        </div>
      </div>
    </div>
  );
};

export default DeleteCollaboratorModal;
